import { type PropsWithChildren, type ReactNode, type UIEventHandler, useContext } from 'react';
import {
  type ButtonProps,
  Dialog,
  OverlayTriggerStateContext,
  type PopoverProps,
  type PressEvent,
  Button as ReactAriaButton,
  Popover as ReactAriaPopover,
} from 'react-aria-components';
import cx from 'clsx';
import { Button } from '@repo/design-system-kit';
import { IconChevronTopOutlined } from '@repo/monochrome-icons';
import { usePreventScrollbarCompensation } from '../../hooks/use-prevent-scrollbar-compensation';
import styles from './popover.module.css';

interface CustomPopoverProps extends Omit<PopoverProps, 'children'> {
  children: ReactNode;
  width?: number;
  height?: number;
  portalContainerId?: string;
}

export interface OverlayTriggerState {
  readonly isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export function PopoverComponent({
  children,
  width = 272,
  height,
  portalContainerId = 'ra-popover-container',
  ...props
}: CustomPopoverProps): ReactNode {
  const portalContainer = document.querySelector(`#${portalContainerId}`) ?? undefined;

  // Prevent React Aria scrollbar compensation layout shift
  usePreventScrollbarCompensation();

  return (
    <ReactAriaPopover
      UNSTABLE_portalContainer={portalContainer}
      offset={4}
      placement="bottom start"
      {...props}
    >
      <Dialog
        className={styles.container}
        data-interactive
        data-testid="popover"
        style={{
          width: `${width.toString()}px`,
          height: height ? `${height.toString()}px` : undefined,
        }}
      >
        {children}
      </Dialog>
    </ReactAriaPopover>
  );
}

interface PopoverHeaderProps extends PropsWithChildren {
  className?: string;
}

function PopoverHeader({ children, className }: PopoverHeaderProps): ReactNode {
  const triggerState = useContext<OverlayTriggerState | null>(OverlayTriggerStateContext);

  return (
    <header className={cx(styles.header, className)} data-testid="popover-header">
      {children}
      <Button
        className={styles['header-button']}
        data-testid="popover-close-button"
        iconOnly
        onPress={triggerState?.close}
        variant="tertiary"
      >
        <IconChevronTopOutlined aria-hidden="true" className={styles.chevron} />
      </Button>
    </header>
  );
}

interface PopoverSectionProps extends PropsWithChildren {
  className?: string;
  onScroll?: UIEventHandler<HTMLElement>;
}

function PopoverSection({ children, className, ...props }: PopoverSectionProps): ReactNode {
  return (
    <section className={cx(styles.section, className)} data-testid="popover-section" {...props}>
      {children}
    </section>
  );
}

interface PopoverButtonProps extends Omit<ButtonProps, 'children'> {
  onPress: (e: PressEvent) => void;
  slots: {
    icon: ReactNode;
    text: ReactNode;
    subtitle?: ReactNode;
  };
  'data-testid'?: string;
  className?: string;
}

function PopoverButton({ onPress, slots, className, ...props }: PopoverButtonProps): ReactNode {
  return (
    <ReactAriaButton
      className={cx(styles.button, 'overlay', className)}
      data-testid="popover-button"
      onPress={onPress}
      {...props}
    >
      {slots.icon}
      <div className={styles.buttonContent}>
        <span className="body-standard" data-testid="button-text">
          {slots.text}
        </span>
        {slots.subtitle ? (
          <span className={styles.subtitle} data-testid="button-subtitle">
            {slots.subtitle}
          </span>
        ) : null}
      </div>
    </ReactAriaButton>
  );
}

export interface PopoverComponentType {
  (props: CustomPopoverProps): ReactNode;
  Section: typeof PopoverSection;
  Button: typeof PopoverButton;
  Header: typeof PopoverHeader;
}

// oxlint-disable-next-line typescript/consistent-type-assertions
export const Popover = Object.assign(PopoverComponent, {
  Section: PopoverSection,
  Button: PopoverButton,
  Header: PopoverHeader,
}) as PopoverComponentType;
